import React, { useState, useEffect, useContext } from 'react'
import EventEmitter from 'mitt'

import * as cognito from '../services/cognito'
import jwt from 'jwt-decode'
import {getCurrentConfiguration} from "../services/requests/system-config/system-configuration";
import {getUser} from "../services/requests/users/users";

export const ROLE = {
  HGC_ADMIN: 'devis_admin',
  OFFICER: 'officer',
}

const defaultState = {
  sessionInfo: {},
  authStatus: 'Loading',
}

export const AuthContext = React.createContext(defaultState)

export const authEventEmitter = new EventEmitter()

export const AuthIsSignedIn = ({ children }) => {
  const { authStatus } = useContext(AuthContext)

  if (authStatus !== 'SignedIn') {
    localStorage.setItem('redirectPath', `${window.location.pathname}${window.location.hash}`)
  }

  return <>{authStatus === 'SignedIn' ? children : null}</>
}

export const AuthIsNotSignedIn = ({ children }) => {
  const { authStatus } = useContext(AuthContext)

  return <>{authStatus === 'SignedOut' ? children : null}</>
}

export const AuthProvider = ({ children }) => {
  const [authStatus, setAuthStatus] = useState('Loading')
  const [sessionInfo, setSessionInfo] = useState({})
  const [attrInfo, setAttrInfo] = useState([])
  const [role, setRole] = useState('')
  const [userInfo, setUserInfo] = useState(null)
  const [systemSettings, setSystemSettings] = useState(null)

  useEffect(() => {
    getSessionInfo()
  }, [setAuthStatus, authStatus])

  useEffect(() => {
    const handleSignout = () => {
      signOut()
    }

    authEventEmitter.on('signout', handleSignout)
    return () => {
      authEventEmitter.off('signout', handleSignout)
    }
  }, [])

  async function getSessionInfo() {
    try {
      const session = await getSession()
      setSessionInfo({
        accessToken: session.accessToken.jwtToken,
        refreshToken: session.refreshToken.token,
        idToken: session.idToken.getJwtToken(),
      })
      const attr = await getAttributes()
      setAttrInfo(attr)
      const accessTokenDecoded = jwt(session.idToken.jwtToken)
      try {
        setRole(accessTokenDecoded['cognito:groups'][0])
        await getUserInfo(accessTokenDecoded.user_id)
        await getSystemSettings()
      } catch (err) {
        setRole('')
      }
      setAuthStatus('SignedIn')
    } catch (err) {
      setAuthStatus('SignedOut')
    }
  }

  if (authStatus === 'Loading') {
    return null
  }

  async function signInWithEmail(username, password) {
    try {
      const res = await cognito.signInWithEmail(username, password)
      setAuthStatus('SignedIn')
      return res
    } catch (err) {
      setAuthStatus('SignedOut')
      throw err
    }
  }

  async function signUpWithEmail(given_name, family_name, email, password) {
    try {
      await cognito.signUpUserWithEmail(given_name, family_name, email, password)
    } catch (err) {
      throw err
    }
  }

  async function completeNewPasswordChallenge(username, password, newPassword) {
    try {
      const res = await cognito.signInWithEmail(username, password)
      if (res.callback === 'newPasswordRequired') {
        delete res.callback
        delete res.email
        delete res.email_verified
        await cognito.completeNewPasswordChallenge(newPassword, res)
      }
      setAuthStatus('SignedIn')
    } catch (err) {
      setAuthStatus('SignedOut')
      throw err
    }
  }

  const asyncLocalStorage = {
    async setItem(key, value) {
      await null
      return localStorage.setItem(key, value)
    },
    async getItem(key) {
      await null
      return localStorage.getItem(key)
    },
  }

  async function signOut() {
    cognito.signOut()
    setAuthStatus('SignedOut')
    setRole('')
    window.localStorage.removeItem('accessToken')
    window.localStorage.removeItem('refreshToken')
    window.localStorage.removeItem('idToken')
    window.localStorage.removeItem('redirectPath')
    const localStorageData = { ...window.localStorage }
    for (const key in localStorageData) {
      asyncLocalStorage.setItem(key, localStorageData[key])
    }
  }

  async function verifyCode(username, code) {
    try {
      await cognito.verifyCode(username, code)
    } catch (err) {
      throw err
    }
  }

  async function getSession() {
    try {
      const session = await cognito.getSession()
      return session
    } catch (err) {
      throw err
    }
  }

  async function getAttributes() {
    try {
      const attr = await cognito.getAttributes()
      return attr
    } catch (err) {
      throw err
    }
  }

  async function setAttribute(attr) {
    try {
      const res = await cognito.setAttribute(attr)
      return res
    } catch (err) {
      throw err
    }
  }

  async function sendCode(username) {
    try {
      await cognito.sendCode(username)
    } catch (err) {
      throw err
    }
  }

  async function forgotPassword(username) {
    try {
      await cognito.forgotPassword(username)
    } catch (err) {
      throw err
    }
  }

  async function confirmPassword(username, code, password) {
    try {
      await cognito.confirmPassword(username, code, password)
    } catch (err) {
      throw err
    }
  }

  async function changePassword(oldPassword, newPassword) {
    try {
      await cognito.changePassword(oldPassword, newPassword)
    } catch (err) {
      throw err
    }
  }

  const getSystemSettings = async () => {
    return getCurrentConfiguration()
      .then(data => {
        setSystemSettings(data.data)
      })
      .catch(err => console.error(err))
  }

  const getUserInfo = async (userId) => {
    return getUser(userId)
      .then(data => {
        setUserInfo(data.data)
      })
      .catch(err => console.error(err))
  }


  const state = {
    authStatus,
    sessionInfo,
    attrInfo,
    role,
    systemSettings,
    setSystemSettings,
    userInfo,
    signUpWithEmail,
    signInWithEmail,
    signOut,
    verifyCode,
    getSession,
    getSessionInfo,
    sendCode,
    forgotPassword,
    confirmPassword,
    changePassword,
    completeNewPasswordChallenge,
    getAttributes,
    setAttribute,
    getSystemSettings,
    getUserInfo
  }

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}

export default AuthProvider
